import {
  splitProps,
  mergeProps,
  Show,
  type JSX,
  type ComponentProps,
  Switch,
  Match,
  createSignal,
  For,
} from "solid-js";

import clsx, { type ClassyComponentProps, type ClassValue } from "~/util/html/style";
import { destinationAttrs, type LinkDestination } from "~/common/destination";
import { Link } from "~/inertia";
import { type InertiaLinkProps } from "~/inertia/Link";
import { Icon, type IconName } from "~/icon";
import { Loader } from "~/common/loader";
import { Popover, PopoverContent } from "~/common/popover";

type ButtonWidth = "default" | "full" | "fit";

type IconRenderer = (iconProps: Partial<ComponentProps<typeof Icon>>) => JSX.Element;

type BaseButtonProps = {
  /** Defaults to `medium` */
  size?: "xs" | "small" | "medium" | "lg";
  variant:
    | "primary"
    | "secondary"
    | "tertiary"
    | "tertiary-danger"
    | "outline-primary"
    | "purple"
    | "danger"
    | "warning"
    | "plain"
    | "plain-muted"
    | "plain-danger"
    | "plain-primary"
    | "plain-purple"
    | "link"
    | "plus-ultra";
  leftIconName?: IconName;
  rightIconName?: IconName;
  square?: boolean;
  renderLeftIcon?: IconRenderer;
  renderRightIcon?: IconRenderer;
  children: JSX.Element;
  width?: ButtonWidth;
  /** When true, the root uses `inline-flex` so the button sits inline with surrounding text */
  inline?: boolean;
  /** Draws the border dashed, for a button that offers a place to put something rather than
   *  running an action. Only the outline variants have a border to dash. */
  dashed?: boolean;
  /** When set, first click shows this content, second click executes onClick */
  confirmContent?: JSX.Element;
  dark?: boolean;
  onClick?: JSX.EventHandler<HTMLElement, MouseEvent>;
  onBlur?: JSX.EventHandler<HTMLElement, FocusEvent>;
  onMouseLeave?: JSX.EventHandler<HTMLElement, MouseEvent>;
};

function isPlainVariant(variant: BaseButtonProps["variant"]): boolean {
  return (
    variant === "plain" ||
    variant === "plain-muted" ||
    variant === "plain-danger" ||
    variant === "plain-primary" ||
    variant === "plain-purple" ||
    variant === "link"
  );
}

function sizeClass(size: BaseButtonProps["size"], square: boolean): string | undefined {
  switch (size) {
    case "xs":
      return square ? "size-input-sm" : "h-input-xs px-3 text-xs";
    case "small":
      return square ? "size-input-sm" : "h-input-sm px-3";
    case "medium":
    case "lg":
      return square ? "size-input" : "h-input px-5";
  }
}

export const SECONDARY_BUTTON_CLASSES =
  "border border-brand-subtle bg-white text-typography-secondary not-disabled:hover:border-typography-primary not-disabled:hover:text-typography-primary focus:bg-brand-active/[.04] high-contrast:border-brand-secondary";

function buttonClasses(
  options: Pick<BaseButtonProps, "size" | "variant" | "inline" | "dashed"> & {
    class?: ClassValue;
    square: boolean;
    width?: ButtonWidth;
  },
): string {
  return clsx(
    // base
    options.inline ? "inline-flex" : "flex",
    "relative items-center gap-2 text-sm font-medium whitespace-nowrap transition-colors ease-in-out",
    !isPlainVariant(options.variant) && "rounded-full",

    // disabled
    // The hairline is what keeps a boxed button reading as a button once it is off: on a white
    // surface the disabled tint alone is barely a shape.
    "disabled:text-brand-border",
    !isPlainVariant(options.variant) && "disabled:border-brand-subtle disabled:bg-brand-light",

    // size
    !isPlainVariant(options.variant) && sizeClass(options.size, options.square),
    isPlainVariant(options.variant) && options.size === "xs" && "text-xs",

    // a square box has no room for the default left-aligned layout
    options.square && "justify-center",

    // width
    options.width === "full" && "w-full justify-center",
    options.width === "fit" && "w-fit",

    // variant
    // Boxed variants without a visible border get a transparent one so they share
    // secondary/danger's box model — toggling variants must never shift layout.
    options.variant === "primary" &&
      "border border-transparent text-white brand-button-bg not-disabled:hover:brand-button-bg-active",
    options.variant === "secondary" && SECONDARY_BUTTON_CLASSES,
    // Pill's purple as a soft fill, for a button whose state that tint names elsewhere.
    options.variant === "purple" &&
      "border border-transparent bg-purple-100 text-purple-700 not-disabled:hover:bg-purple-200",
    options.variant === "danger" &&
      "border border-brand-danger bg-white not-disabled:hover:bg-brand-danger-light",
    // Mirrors the Hotwire ui_button :warning variant, so a control that exists in both
    // schedules reads the same in either.
    options.variant === "warning" &&
      "border border-brand-warning bg-white text-typography-primary not-disabled:hover:bg-brand-warning-light",
    options.variant === "plain" &&
      "text-typography-secondary not-disabled:hover:text-typography-primary data-dark:text-typography-secondary-dark data-dark:not-disabled:hover:text-typography-primary-dark",
    options.variant === "plain-muted" &&
      "text-typography-tertiary not-disabled:hover:text-typography-primary",
    options.variant === "plain-danger" &&
      "text-brand-danger not-disabled:hover:text-brand-danger-hover",
    options.variant === "plain-primary" &&
      "not-disabled:hover:text-brand-primary-hover text-brand-primary",
    options.variant === "plain-purple" && "text-purple-700 not-disabled:hover:text-purple-800",
    options.variant === "link" && "text-brand-link not-disabled:hover:text-brand-link-hover",
    options.variant === "tertiary" &&
      "border border-transparent bg-transparent text-brand-primary not-disabled:hover:bg-brand-hover",
    options.variant === "tertiary-danger" &&
      "border border-transparent bg-transparent text-brand-danger not-disabled:hover:bg-brand-danger-light",
    options.variant === "outline-primary" &&
      "border border-brand-primary bg-white text-typography-primary not-disabled:hover:bg-brand-primary/10",
    options.variant === "plus-ultra" &&
      "border border-transparent bg-linear-to-r from-indigo-100 to-purple-200 font-semibold text-indigo-700 shadow-md shadow-purple-100 not-disabled:hover:from-indigo-200 not-disabled:hover:to-purple-300 not-disabled:hover:shadow-lg not-disabled:hover:shadow-purple-100",

    options.dashed && "border-dashed",

    // custom
    options.class,
  );
}

function createButtonProps(props: BaseButtonProps & { class?: ClassValue; width?: ButtonWidth }) {
  const withDefaults = mergeProps({ size: "medium" as const }, props);

  const [showConfirm, setShowConfirm] = createSignal(false);

  const [contentProps, restFromContentProps] = splitProps(withDefaults, [
    "leftIconName",
    "rightIconName",
    "renderLeftIcon",
    "renderRightIcon",
    "children",
  ]);

  const [local, buttonProps] = splitProps(restFromContentProps, [
    "size",
    "variant",
    "class",
    "square",
    "width",
    "inline",
    "dashed",
    "dark",
    "confirmContent",
    "onClick",
    "onBlur",
    "onMouseLeave",
  ]);

  const onClick: JSX.EventHandler<HTMLElement, MouseEvent> = (e) => {
    if (local.confirmContent && !showConfirm()) {
      e.preventDefault();
      setShowConfirm(true);
    } else {
      if (showConfirm()) setShowConfirm(false);
      local.onClick?.(e);
    }
  };

  const onBlur: JSX.EventHandler<HTMLElement, FocusEvent> = (e) => {
    if (local.confirmContent && showConfirm()) {
      setShowConfirm(false);
    }
    local.onBlur?.(e);
  };

  const onMouseLeave: JSX.EventHandler<HTMLElement, MouseEvent> = (e) => {
    if (local.confirmContent && showConfirm()) {
      setShowConfirm(false);
    }
    local.onMouseLeave?.(e);
  };

  const root = mergeProps(buttonProps, {
    get class() {
      const classProps = mergeProps(local, {
        square: Boolean(
          local.square ||
            (!contentProps.children && (contentProps.leftIconName || contentProps.renderLeftIcon)),
        ),
        width: local.width ?? "default",
      });
      return buttonClasses(classProps);
    },
    get "data-dark"() {
      return local.dark ? true : undefined;
    },
    onClick,
    onBlur,
    onMouseLeave,
  });

  const content = mergeProps(
    {
      get confirmContent() {
        return showConfirm() ? props.confirmContent : undefined;
      },
    },
    contentProps,
    {
      iconProps: {
        get size() {
          if (isPlainVariant(local.variant) && contentProps.children) {
            return "xs";
          }
          switch (local.size) {
            case "lg":
              return "md";
            case "small":
              return "xs";
            default:
              return "sm";
          }
        },
      },
    },
  );

  return { root, content };
}

function ButtonContent(props: {
  iconProps: {
    size: ComponentProps<typeof Icon>["size"];
  };
  leftIconName?: IconName;
  rightIconName?: IconName;
  renderLeftIcon?: IconRenderer;
  renderRightIcon?: IconRenderer;
  children: JSX.Element;
  confirmContent?: JSX.Element;
}) {
  return (
    <>
      <Switch>
        <Match when={props.renderLeftIcon}>
          {(renderLeftIcon) => renderLeftIcon()(props.iconProps)}
        </Match>
        <Match when={props.leftIconName}>
          {(iconName) => <Icon {...props.iconProps} name={iconName()} />}
        </Match>
      </Switch>

      <Show when={props.confirmContent} fallback={props.children}>
        {props.confirmContent}
      </Show>

      <Switch>
        <Match when={props.renderRightIcon}>
          {(renderRightIcon) => renderRightIcon()(props.iconProps)}
        </Match>
        <Match when={props.rightIconName}>
          {(iconName) => <Icon {...props.iconProps} name={iconName()} />}
        </Match>
      </Switch>
    </>
  );
}

export type ButtonProps = BaseButtonProps &
  Omit<ClassyComponentProps<"button">, "onClick" | "onBlur" | "onMouseLeave"> & {
    /** When true, hides children (preserving width) and overlays a Loader. */
    loading?: boolean;
  };
export function Button(props: ButtonProps) {
  const { root, content } = createButtonProps(props);
  return (
    <button {...root} type={props.type || "button"} aria-busy={props.loading || undefined}>
      <Show when={props.loading}>
        <span class="pointer-events-none absolute inset-0 flex items-center justify-center">
          <Loader
            size={loaderSizeFor(props.size)}
            variant={props.variant === "primary" ? "light" : "default"}
          />
        </span>
      </Show>
      <div class={clsx("contents", props.loading && "invisible")}>
        <ButtonContent {...content} />
      </div>
    </button>
  );
}

function loaderSizeFor(size: BaseButtonProps["size"]): "sm" | "md" {
  return size === "lg" ? "md" : "sm";
}

type ButtonAProps = BaseButtonProps &
  Omit<ClassyComponentProps<"a">, "onClick" | "onBlur" | "onMouseLeave"> & {
    destination?: LinkDestination;
    turbo?: boolean;
  };
export function ButtonA(props: ButtonAProps) {
  const [local, buttonProps] = splitProps(props, ["turbo", "destination"]);
  const { root, content } = createButtonProps(buttonProps);

  return (
    <a {...root} {...destinationAttrs(local.destination)} data-turbo={local.turbo ?? false}>
      <ButtonContent
        {...content}
        rightIconName={
          content.rightIconName || (local.destination === "external" ? "external-link" : undefined)
        }
      />
    </a>
  );
}

type ButtonLinkProps<T extends keyof JSX.IntrinsicElements = "a"> = BaseButtonProps &
  Omit<InertiaLinkProps<T>, "onClick" | "onBlur" | "onMouseLeave">;
export function ButtonLink<T extends keyof JSX.IntrinsicElements = "a">(props: ButtonLinkProps<T>) {
  const { root, content } = createButtonProps(props);
  return (
    <Link {...root}>
      <ButtonContent {...content} />
    </Link>
  );
}

type ToIconButton<T> = Omit<
  T,
  "leftIconName" | "rightIconName" | "renderLeftIcon" | "renderRightIcon" | "children" | "loading"
> & {
  iconName: IconName;
};

export function IconButton(props: ToIconButton<ButtonProps>) {
  const [local, rest] = splitProps(props, ["iconName"]);
  return <Button {...rest} leftIconName={local.iconName} children={null} />;
}

export function IconButtonA(props: ToIconButton<ButtonAProps>) {
  const [local, rest] = splitProps(props, ["iconName"]);
  return <ButtonA {...rest} leftIconName={local.iconName} children={null} />;
}

export function IconButtonLink<T extends keyof JSX.IntrinsicElements = "a">(
  props: ToIconButton<ButtonLinkProps<T>>,
) {
  const [local, rest] = splitProps(props, ["iconName"]);
  return <ButtonLink {...rest} leftIconName={local.iconName} children={null} />;
}

// Split Button
export type SplitButtonOption = {
  label: string;
  icon?: string;
  onClick: () => void;
  variant?: "default" | "danger";
};

type SplitButtonProps = {
  children: JSX.Element;
  options: SplitButtonOption[];
  variant?: BaseButtonProps["variant"];
  size?: BaseButtonProps["size"];
  disabled?: boolean;
  onClick?: () => void;
  /** Screen-reader name for the caret half, which has no visible label. */
  menuLabel?: string;
};

export function SplitButton(props: SplitButtonProps) {
  const merged = mergeProps(
    { variant: "primary" as const, size: "medium" as const, menuLabel: "More actions" },
    props,
  );

  return (
    <div class="flex">
      <Button
        variant={merged.variant}
        size={merged.size}
        disabled={merged.disabled}
        onClick={merged.onClick}
        class="rounded-r-none border-r-0"
      >
        {merged.children}
      </Button>

      <Popover
        placement="bottom-end"
        enabled={!merged.disabled}
        renderTrigger={(trigger) => (
          <Button
            ref={trigger.props.ref}
            variant={merged.variant}
            size={merged.size}
            disabled={merged.disabled}
            aria-label={merged.menuLabel}
            aria-haspopup="menu"
            onClick={() => trigger.setOpen((prev) => !prev)}
            // px-2! overrides base padding to make the dropdown trigger narrower while keeping matching height
            class="rounded-l-none border-l border-l-brand-subtle px-2!"
            rightIconName="chevronDown"
          >
            {null}
          </Button>
        )}
      >
        {(content) => (
          <PopoverContent>
            <For each={merged.options}>
              {(option) => (
                <button
                  type="button"
                  class={clsx(
                    "block w-full cursor-pointer px-5 py-3 text-left text-sm hover:bg-brand-hover",
                    option.variant === "danger" && "text-brand-danger",
                  )}
                  onClick={() => {
                    option.onClick();
                    content.setOpen(false);
                  }}
                >
                  <Show when={option.icon} fallback={option.label}>
                    {(src) => (
                      <span class="inline-flex items-center gap-1.5">
                        <img src={src()} alt="" class="size-4" />
                        {option.label}
                      </span>
                    )}
                  </Show>
                </button>
              )}
            </For>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
}
